import Route from 'route-parser';
import {
    arrayOrderBy,
    compareStringsCaseInsensitive,
    iterableMapToArray, stringEnsurePrefix, stringTrimSuffix, urlJoin, urlParseQueryString,
} from '../';
import {assertDefined} from '../assertions';

export namespace RoutesManager {

    export type ConvertToPathOrQueryParams<T> = {
        [K in keyof T]:
            Required<T>[K] extends boolean ? 'true' | 'false' :
            Required<T>[K] extends string ? string :
            T[K];
    }

    export interface Entry<ROUTE_INFO, CONTEXT> {
        rule: RouteRule<ROUTE_INFO, CONTEXT, any>;
        path: string;
        route: Route;
    }

    export type RouteRule<ROUTE_INFO, CONTEXT, TParams extends {}> =
        WithoutContext.RouteRule<ROUTE_INFO, TParams> | WithContext.RouteRule<ROUTE_INFO, CONTEXT, TParams>;

    export type RouteQueryParams<TParams extends {}> =
        Record<string, string | QueryParamInfo<TParams>>

    export interface RouteRuleBase<ROUTE_INFO, TParams extends {}> {
        path: string;
        queryParams?: RouteQueryParams<TParams>;
        resolveParams: (routeinfo: ROUTE_INFO) => TParams | null,
    }

    export interface QueryParamInfo<TParams extends {}> {
        param: keyof TParams;
        optional?: boolean;
        validator?: (value: string) => boolean;
    }

    export interface Options {
        basePath?: string;
    }

    export namespace WithContext {

        export type RouteInfoResolvingFunction<ROUTE_INFO, CONTEXT, TParams extends {}> =
            (match: Record<keyof TParams, any>, context: CONTEXT) => ROUTE_INFO;

        export interface RouteRule<ROUTE_INFO, CONTEXT, TParams extends {}> extends RouteRuleBase<ROUTE_INFO, TParams> {
            resolveRouteInfoFromParams: RouteInfoResolvingFunction<ROUTE_INFO, CONTEXT, TParams>,
            test?: (context: CONTEXT) => boolean;
        }
    }

    export namespace WithoutContext {

        export type RouteInfoResolvingFunction<ROUTE_INFO, TParams extends {}> =
            (match: Record<keyof TParams, any>) => ROUTE_INFO;

        export interface RouteRule<ROUTE_INFO, TParams extends {}> extends RouteRuleBase<ROUTE_INFO, TParams> {
            resolveRouteInfoFromParams: RouteInfoResolvingFunction<ROUTE_INFO, TParams>,
        }
    }
}

interface IRoutesManagerCommon<ROUTE_INFO> {
    getBasePath () : string;
    resolvePath (routeInfo: ROUTE_INFO) : string;
    tryResolvePath (routeinfo: ROUTE_INFO) : string | null;
}

export namespace IRoutesManager {

    export interface Initialized<ROUTE_INFO> {
        resolveRouteInfo (path: string, query: Record<string, string> | string) : ROUTE_INFO | null
    }

}

export interface IRoutesManager<ROUTE_INFO> extends IRoutesManagerCommon<ROUTE_INFO> {

    registerRoute<TParams extends {}> (routeRule: RoutesManager.WithoutContext.RouteRule<ROUTE_INFO, TParams>) : void;

    resolveRouteInfo (path: string, query: Record<string, string> | string) : ROUTE_INFO | null
}

export namespace IRoutesManagerWithContext {

    export interface Initialized<ROUTE_INFO, CONTEXT> {
        resolveRouteInfo (path: string, query: Record<string, string> | string, context: CONTEXT) : ROUTE_INFO | null;
    }

}

export interface IRoutesManagerWithContext<ROUTE_INFO, CONTEXT> extends IRoutesManagerCommon<ROUTE_INFO> {

    registerRoute<TParams extends {}> (routeRule: RoutesManager.WithContext.RouteRule<ROUTE_INFO, CONTEXT, TParams>) : void;

    resolveRouteInfo (path: string, query: Record<string, string> | string, context: CONTEXT) : ROUTE_INFO | null;
}

export class RoutesManager<ROUTE_INFO, CONTEXT = unknown> {

    private entries: RoutesManager.Entry<ROUTE_INFO, CONTEXT>[] = [];
    private basePath: string;

    protected constructor(
        private withContext: boolean,
        options: RoutesManager.Options = {}
    ) {
        const {
            basePath = '/'
        } = options;

        this.basePath = stringEnsurePrefix(basePath, '/');
    }

    public static create<ROUTE_INFO> (options: RoutesManager.Options = {}) : IRoutesManager<ROUTE_INFO> {
        return new RoutesManager<ROUTE_INFO>(false, options)
    }

    public static createWithContext<ROUTE_INFO, CONTEXT> (options: RoutesManager.Options = {}) : IRoutesManagerWithContext<ROUTE_INFO, CONTEXT> {
        return new RoutesManager<ROUTE_INFO, CONTEXT>(true, options)
    }

    public getBasePath () {
        return this.basePath;
    }

    private normalizePath (path: string) {
        return path === '/' ? path : stringTrimSuffix(path, '/')  + '(/)'
    }

    public registerRoute<TParams extends {}> (
        routeRule: RoutesManager.RouteRule<ROUTE_INFO, CONTEXT, TParams>
    ) {

        const {
            path
        } = routeRule;

        const normalizedPath = this.normalizePath(urlJoin(this.basePath, path));
        const route = new Route(normalizedPath);

        this.entries.push({
            route: route,
            rule: routeRule,
            path: normalizedPath
        });
    }

    public resolvePath (routeInfo: ROUTE_INFO) : string {
        const path = this.tryResolvePath(routeInfo);

        if (path === null)
        {
            throw new Error(`Couldn't resolve path for route: ${JSON.stringify(routeInfo)}`);
        }

        return path;
    }

    public tryResolvePath (routeInfo: ROUTE_INFO) : string | null {
        for (const entry of this.entries)
        {
            const {
                route,
                rule
            } = entry;

            const params = rule.resolveParams(routeInfo);
            if (params !== null)
            {
                const path = route.reverse(params);

                if (path === false)
                {
                    throw new Error();
                }

                const queryParams = rule.queryParams;

                let searchStr = '';
                if (queryParams)
                {
                    const orderedEntries = arrayOrderBy(Object.entries(queryParams), entry => entry[0], compareStringsCaseInsensitive);

                    searchStr = '?' + iterableMapToArray(orderedEntries, ([queryParam, paramInfo], skip) => {
                        const paramValue = typeof paramInfo === 'string' ? paramInfo : params[paramInfo.param as string];

                        if (paramValue === undefined)
                        {
                            return skip;
                        }

                        return queryParam + '=' + encodeURIComponent(paramValue);
                    }).join('&');
                }

                return (path === '/' ? path : stringTrimSuffix(path, '/')) + (searchStr === '?' ? '' : searchStr);
            }
        }

        return null;
    }

    public resolveRouteInfo (path: string, query: Record<string, string> | string, context?: CONTEXT) : ROUTE_INFO | null {

        let queryParams: Record<string, string>;

        if (typeof query === 'string')
        {
            queryParams = urlParseQueryString(query);
        }
        else
        {
            queryParams = query;
        }

        const withContext = this.withContext;
        for (const entry of this.entries)
        {
            const {
                route,
                rule
            } = entry;

            const ruleWithContext = withContext ?
                rule as RoutesManager.WithContext.RouteRule<ROUTE_INFO, CONTEXT, any> :
                undefined;

            const ruleWithoutContext = !withContext ?
                rule as RoutesManager.WithoutContext.RouteRule<ROUTE_INFO, any> :
                undefined;

            if (ruleWithContext) {
                const ruleTest = ruleWithContext.test;

                if (ruleTest && !ruleTest(assertDefined(context))) {
                    continue;
                }
            }

            const params = route.match(path);
            if (params !== false)
            {
                const queryParamsMatch = matchRuleQueryParams(rule, queryParams);

                if (queryParamsMatch !== false)
                {
                    if (ruleWithoutContext) {
                        return ruleWithoutContext.resolveRouteInfoFromParams({
                            ...params,
                            ...queryParamsMatch
                        });
                    } else {
                        return assertDefined(ruleWithContext).resolveRouteInfoFromParams({
                            ...params,
                            ...queryParamsMatch
                        }, assertDefined(context));
                    }


                }
            }
        }

        return null;
    }

    public static queryParamRequired<P extends string> (routeParamName: P, validator?: (value: string) => boolean) {
        return {
            param: routeParamName,
            validator: validator
        };
    }

    public static queryParamOptional<P extends string> (routeParamName: P, validator?: (value: string) => boolean) {
        return {
            param: routeParamName,
            optional: true,
            validator: validator
        };
    }
}

function matchRuleQueryParams<ROUTE_INFO> (rule: RoutesManager.RouteRule<ROUTE_INFO, any, any>, queryParams: Record<string, string> | undefined) {

    const ruleQueryParams = rule.queryParams;

    const match: Record<string, string | undefined> = {};

    if (ruleQueryParams)
    {
        for (const [paramName, value] of Object.entries(ruleQueryParams))
        {
            const paramValue = queryParams ? queryParams[paramName] : undefined;

            if (typeof value === 'string') {
                if (value !== paramValue) {
                    return false;
                }
            } else {
                if (paramValue === undefined && !value.optional) {
                    return false;
                }

                if (paramValue !== undefined && value.validator !== undefined && !value.validator(paramValue)) {
                    return false;
                }

                match[value.param as string] = paramValue;
            }

        }
    }

    return match;
}
